/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { FirebaseListFactory } from 'src/app/common/fancy-firebase/services/factories';
import { FancyIdJoin } from 'src/app/common/fancy-firebase/base/FancyIdJoin';
import { CustomFieldsApi } from 'src/app/app-data/state/CustomFieldsApi';
import { CustomFieldDefinitions } from 'src/app/app-data/state/CustomFieldDefinitions';
import { CustomFieldDropDownDefinitions } from 'src/app/app-data/state/CustomFieldDropDownDefinitions';
import { CustomFieldTicketValues } from 'src/app/app-data/state/CustomFieldTicketValues';
import { CustomFieldDropDownDefinitionList } from 'src/app/common/fancy-firebase/lists/CustomFieldDropDownDefinitionList';
import { CustomFieldDefinitionList } from 'src/app/common/fancy-firebase/lists/CustomFieldDefinitionList';
import { CustomFieldTicketValueList } from 'src/app/common/fancy-firebase/lists/CustomFieldTicketValueList';
import { OrdinalCalculatorFactory } from '../OrdinalCalculatorFactory.service';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';

import firebase from 'firebase/compat/app';

@Injectable({
	providedIn: 'root',
})
export class FancyFirebaseCustomFieldsApiBuilder {
	constructor(
		private firebaseListFactory: FirebaseListFactory,
		private ordinalCalculatorFactory: OrdinalCalculatorFactory
	) {}

	public getCustomFieldsApi(projectId: string, planId: string, projectMemberId: string): CustomFieldsApi {
		// dropdown list
		const customFieldsPath = fancyFirebaseGetters.getCustomFieldsPath(projectId);
		const ticketCustomFieldsPath = fancyFirebaseGetters.getTicketCustomFieldsPath(projectId, planId);
		const projectMemberTicketCustomFieldsPath = fancyFirebaseGetters.getProjectMemberTicketCustomFieldsPath(
			projectId,
			projectMemberId
		);

		const customFieldDropDownDefinitionList = this.firebaseListFactory.create(
			CustomFieldDropDownDefinitionList,
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(customFieldsPath, projectId)
				.child('listOfValuesDefinitions')
		);
		const customFieldDropDownJoiner = new FancyIdJoin('listOfValuesId', customFieldDropDownDefinitionList);
		const customFieldDropDownDefinitions = new CustomFieldDropDownDefinitions(
			customFieldDropDownDefinitionList
		);

		// definitions
		const definitionList = this.firebaseListFactory.create(
			CustomFieldDefinitionList,
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(customFieldsPath, projectId)
				.child('definitions'),
			customFieldDropDownJoiner
		);
		const customFieldDefinitions = new CustomFieldDefinitions(definitionList);

		// ticket values
		const customFieldTicketValueList = this.firebaseListFactory.create(
			CustomFieldTicketValueList,
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(ticketCustomFieldsPath, projectId)
		);
		const customFieldTicketValues = new CustomFieldTicketValues(customFieldTicketValueList);

		// on-deck/floating ticket values
		const customFieldOnDeckTicketValueList = this.firebaseListFactory.create(
			CustomFieldTicketValueList,
			fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
				projectMemberTicketCustomFieldsPath,
				projectId
			)
		);
		const customFieldOnDeckTicketValues = new CustomFieldTicketValues(customFieldOnDeckTicketValueList);

		customFieldDropDownDefinitions.addDestroyer(() => customFieldDropDownJoiner.destroy());

		return new CustomFieldsApi(
			customFieldDefinitions,
			customFieldDropDownDefinitions,
			customFieldTicketValues,
			customFieldOnDeckTicketValues,
			this.ordinalCalculatorFactory,
			planId
		);
	}

	public getCustomFieldDefinitions(projectId: string): CustomFieldDefinitions {
		// dropdown list
		const customFieldsPath = fancyFirebaseGetters.getCustomFieldsPath(projectId);

		const customFieldDropDownDefinitionList = this.firebaseListFactory.create(
			CustomFieldDropDownDefinitionList,
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(customFieldsPath, projectId)
				.child('listOfValuesDefinitions')
		);
		const customFieldDropDownJoiner = new FancyIdJoin('listOfValuesId', customFieldDropDownDefinitionList);

		// definitions
		const definitionList = this.firebaseListFactory.create(
			CustomFieldDefinitionList,
			fancyFirebaseReferencer
				.getLegacyRef<firebase.database.Reference>(customFieldsPath, projectId)
				.child('definitions'),
			customFieldDropDownJoiner
		);
		const customFieldDefinitions = new CustomFieldDefinitions(definitionList);
		return customFieldDefinitions;
	}
}
